import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const ROLES = [
  { v: "admin", label: "Administrator" },
  { v: "disponent", label: "Disponent" },
  { v: "fahrer", label: "Fahrer" },
];

const empty = { username: "", password: "", role: "disponent", full_name: "", active: true, linked_driver_id: "" };

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const [u, d] = await Promise.all([api.get("/users"), api.get("/drivers")]);
      setUsers(u.data);
      setDrivers(d.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (u) => { setEditing(u); setForm({ ...empty, ...u, password: "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, linked_driver_id: form.linked_driver_id || null };
      if (editing) {
        if (!payload.password) delete payload.password;
        delete payload.username; // immutable
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Benutzer aktualisiert");
      } else {
        await api.post("/users", payload);
        toast.success("Benutzer angelegt");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success(`Benutzer "${confirmDelete.username}" gelöscht`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // The protected "developer" super-admin can only be managed by another developer.
  const isProtected = (u) => u.role === "developer" || u.protected;
  const canManage = (u) => currentUser.role === "developer" || !isProtected(u);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold">Benutzerverwaltung</h1>
          <p className="text-sm text-muted-foreground">Verwalten Sie Benutzerkonten und Rollen.</p>
        </div>
        <Button onClick={startCreate} data-testid="users-create-button">
          <Plus className="h-4 w-4 mr-1" /> Neuer Benutzer
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2">Benutzername</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Rolle</th>
              <th className="text-left px-4 py-2">Aktiv</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border row-hover">
                <td className="px-4 py-2 font-mono">{u.username}</td>
                <td className="px-4 py-2">{u.full_name || "—"}</td>
                <td className="px-4 py-2 capitalize">
                  {u.role === "developer"
                    ? <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-200">Developer</span>
                    : u.role}
                </td>
                <td className="px-4 py-2">
                  <Switch checked={u.active} onCheckedChange={() => toggle(u)} disabled={!canManage(u)} data-testid={`user-active-toggle-${u.id}`} />
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(u)} disabled={!canManage(u)} data-testid={`user-edit-${u.id}`} aria-label="Bearbeiten">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDelete(u)}
                      disabled={u.id === currentUser.id || !canManage(u)}
                      title={isProtected(u) ? "Geschützter Benutzer – kann nicht gelöscht werden" : (u.id === currentUser.id ? "Eigenen Benutzer können Sie nicht löschen" : "Löschen")}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                      data-testid={`user-delete-${u.id}`}
                      aria-label="Löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Benutzer bearbeiten" : "Neuer Benutzer"}</DialogTitle><DialogDescription>Konto und Rolle festlegen.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>Benutzername</Label>
              <Input value={form.username} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username-input" />
            </div>
            <div>
              <Label>Vollständiger Name</Label>
              <Input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname-input" />
            </div>
            <div>
              <Label>{editing ? "Neues Passwort (optional)" : "Passwort"}</Label>
              <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div>
              <Label>Rolle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.role === "fahrer" && (
              <div>
                <Label>Verknüpftes Fahrerprofil</Label>
                <Select value={form.linked_driver_id || "none"} onValueChange={(v) => setForm({ ...form, linked_driver_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="user-driver-select"><SelectValue placeholder="Fahrer wählen" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— keiner —</SelectItem>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.firstname} {d.lastname}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-sm">Aktiv</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} data-testid="user-save-button">{editing ? "Speichern" : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Benutzer <span className="font-mono font-semibold">{confirmDelete?.username}</span>{" "}
              wird unwiderruflich gelöscht. Vorhandene Audit-Log-Einträge bleiben erhalten.
              <br />
              Falls Sie den Zugang nur vorübergehend sperren möchten, deaktivieren Sie den Benutzer stattdessen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-red-600 hover:bg-red-700"
              data-testid="user-delete-confirm"
            >
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
